import request from '@/plugins/request';
import { IBoolResult, IExecuteResult, IStringResult } from '@/types/dto';
import { IHost, IHostBackup, IHostCert, IHostDomain } from '@/types/models';
import utils from '@/utils';
import { AxiosResponse } from 'axios';

const $url = '/admin/hosts';
const $urlShell = '/admin/hosts/actions/shell';
const $urlUpdateDomainsCert = '/admin/hosts/actions/updateDomainsCert';
const $urlCredentials = '/admin/hosts/credentials';
const $urlStart = '/admin/hosts/actions/start';
const $urlStop = '/admin/hosts/actions/stop';
const $urlReload = '/admin/hosts/actions/reload';
const $urlGetDomains = '/admin/hosts/actions/getDomains';
const $urlSetDomain = '/admin/hosts/actions/setDomain';
const $urlSetDomainQuery = '/admin/hosts/actions/setDomainQuery';
const $urlBackups = '/admin/hosts/backups';
const $urlBackupsRestore = '/admin/hosts/backups/actions/restore';
const $urlGetCerts = '/admin/hosts/actions/getCerts';
const $urlResetPassword = '/admin/hosts/resetPassword';

interface IHostRequest {
  hostId: number;
}

export interface IGetRequest {
  nodeName: string;
  dateFrom: string;
  dateTo: string;
  keyword: string;
  orderByIdDesc: boolean;
}

interface IGetResult {
  hosts: IHost[];
  nodeNames: string[];
}

export interface IShellResult {
  docker: string;
}

interface ICredentialsGetResult {
  isHongKong: boolean;
  securityKey: string;
  ftpPassword: string;
  databaseHost: string;
  databaseUser: string;
  databasePassword: string;
  databaseName: string;
}

interface ICredentialsSubmitRequest {
  hostId: number;
  securityKey: string;
  ftpPassword: string;
  databaseUser: string;
  databasePassword: string;
  databaseName: string;
}

export interface IAddRequest {
  nodeName: string;
  userName: string;
  hostType: string;
  expirationDate: Date;
}

export interface IEditRequest {
  hostId: number;
  expirationDate: Date;
}

interface IGetDomainsResult {
  hostName: string;
  nodeName: string;
  isHongKong: boolean;
  uniqueName: string;
  nickName: string;
  securityKey: string;
  domains: IHostDomain[];
}

interface ISetDomainRequest {
  userName: string;
  hostId: number;
  siteIds: number[];
  siteId: number;
  prefix: string;
  domain: string;
  https: boolean;
  background: boolean;
}

interface IBackupsResult {
  hostName: string;
  nodeName: string;
  isHongKong: boolean;
  uniqueName: string;
  nickName: string;
  backups: IHostBackup[];
}

interface IBackupRequest {
  userName: string;
  hostId: number;
  backupId: number;
}

interface IGetCertsResult {
  hostName: string;
  nodeName: string;
  isHongKong: boolean;
  uniqueName: string;
  nickName: string;
  domains: IHostDomain[];
  certs: IHostCert[];
}

interface IResetPasswordGetResult {
  securityKey: string;
  guid: string;
}

interface IResetPasswordSubmitRequest {
  hostId: number;
  userName: string;
  password: string;
}

export default {
  get(req: IGetRequest, success: (res: IGetResult) => void) {
    utils.loading(true);
    request
      .get($url, {
        params: req,
      })
      .then((response: AxiosResponse<IGetResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      })
      .then(() => {
        utils.loading(false);
      });
  },

  add(req: IAddRequest, success: (res: IBoolResult) => void) {
    utils.loading(true);
    request
      .post($url, req)
      .then((response: AxiosResponse<IBoolResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      })
      .then(() => {
        utils.loading(false);
      });
  },

  edit(req: IEditRequest, success: (res: IBoolResult) => void) {
    utils.loading(true);
    request
      .put($url, req)
      .then((response: AxiosResponse<IBoolResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      })
      .then(() => {
        utils.loading(false);
      });
  },

  shell(hostId: number, success: (res: IShellResult) => void) {
    utils.loading(true);
    request
      .post($urlShell, {
        hostId,
      })
      .then((response: AxiosResponse<IShellResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      })
      .then(() => {
        utils.loading(false);
      });
  },

  updateDomainsCert(hostId: number, success: (res: IBoolResult) => void) {
    utils.loading(true);
    request
      .post($urlUpdateDomainsCert, {
        hostId,
      })
      .then((response: AxiosResponse<IBoolResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      })
      .then(() => {
        utils.loading(false);
      });
  },

  delete(hostId: number, success: (res: IBoolResult) => void) {
    utils.loading(true);
    request
      .delete($url, {
        data: {
          hostId,
        },
      })
      .then((response: AxiosResponse<IBoolResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      })
      .then(() => {
        utils.loading(false);
      });
  },

  credentialsGet(userName: string, hostId: number, success: (res: ICredentialsGetResult) => void) {
    utils.loading(true);
    request
      .get($urlCredentials, {
        params: {
          userName,
          hostId,
        },
      })
      .then((response: AxiosResponse<ICredentialsGetResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      })
      .then(() => {
        utils.loading(false);
      });
  },

  credentialsSubmit(req: ICredentialsSubmitRequest, success: (res: IBoolResult) => void) {
    utils.loading(true);
    request
      .post($urlCredentials, req)
      .then((response: AxiosResponse<IBoolResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      })
      .then(() => {
        utils.loading(false);
      });
  },

  start(req: IHostRequest, success: (res: IBoolResult) => void) {
    utils.loading(true);
    request
      .post($urlStart, req)
      .then((response: AxiosResponse<IBoolResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      })
      .then(() => {
        utils.loading(false);
      });
  },

  stop(req: IHostRequest, success: (res: IBoolResult) => void) {
    utils.loading(true);
    request
      .post($urlStop, req)
      .then((response: AxiosResponse<IBoolResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      })
      .then(() => {
        utils.loading(false);
      });
  },

  reload(req: IHostRequest, success: (res: IBoolResult) => void) {
    utils.loading(true);
    request
      .post($urlReload, req)
      .then((response: AxiosResponse<IBoolResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      })
      .then(() => {
        utils.loading(false);
      });
  },

  getDomains(userName: string, hostId: number, success: (res: IGetDomainsResult) => void) {
    utils.loading(true);
    request
      .post($urlGetDomains, {
        userName,
        hostId,
      })
      .then((response: AxiosResponse<IGetDomainsResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
        utils.loading(false);
      });
  },

  setDomain(req: ISetDomainRequest, success: (res: IStringResult) => void) {
    request
      .post($urlSetDomain, req)
      .then((response: AxiosResponse<IStringResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      });
  },

  setDomainQuery(guid: string, success: (res: IExecuteResult) => void) {
    request
      .post($urlSetDomainQuery, {
        guid,
      })
      .then((response: AxiosResponse<IExecuteResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      });
  },

  backupsGet(userName: string, hostId: number, success: (res: IBackupsResult) => void) {
    utils.loading(true);
    request
      .get($urlBackups, {
        params: {
          userName,
          hostId,
        },
      })
      .then((response: AxiosResponse<IBackupsResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      })
      .then(() => {
        utils.loading(false);
      });
  },

  backupsSubmit(userName: string, hostId: number, success: (res: IBoolResult) => void) {
    utils.loading(true);
    request
      .post($urlBackups, {
        userName,
        hostId,
      })
      .then((response: AxiosResponse<IBoolResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      })
      .then(() => {
        utils.loading(false);
      });
  },

  backupsDelete(req: IBackupRequest, success: (res: IBoolResult) => void) {
    utils.loading(true);
    request
      .delete($urlBackups, {
        data: req,
      })
      .then((response: AxiosResponse<IBoolResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      })
      .then(() => {
        utils.loading(false);
      });
  },

  backupsRestore(req: IBackupRequest, success: (res: IBoolResult) => void) {
    utils.loading(true);
    request
      .post($urlBackupsRestore, req)
      .then((response: AxiosResponse<IBoolResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      })
      .then(() => {
        utils.loading(false);
      });
  },

  getCerts(userName: string, hostId: number, success: (res: IGetCertsResult) => void) {
    utils.loading(true);
    request
      .post($urlGetCerts, {
        userName,
        hostId,
      })
      .then((response: AxiosResponse<IGetCertsResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      })
      .then(() => {
        utils.loading(false);
      });
  },

  resetPasswordGet(hostId: number, success: (res: IResetPasswordGetResult) => void) {
    utils.loading(true);
    request
      .get($urlResetPassword, {
        params: { hostId },
      })
      .then((response: AxiosResponse<IResetPasswordGetResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      })
      .then(() => {
        utils.loading(false);
      });
  },

  resetPasswordSubmit(req: IResetPasswordSubmitRequest, success: (res: IBoolResult) => void) {
    utils.loading(true);
    request
      .post($urlResetPassword, req)
      .then((response: AxiosResponse<IBoolResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      })
      .then(() => {
        utils.loading(false);
      });
  },
};
